import { Link } from "react-router-dom";
import clsx from "clsx";
import { BREAKPOINTS } from "@/constants/breakpoints";
import styles from "./Banner.module.css";

interface BannerProps {
  banner: {
    image: {
      default: string;
      desktop: string;
    };
    title: React.ReactNode;
    description?: React.ReactNode;
    link?: {
      href: string;
      text: string;
    };
  };
  priority?: boolean; // 首屏圖優先載入
  heightClasses?: string; // Tailwind utility class，用於設定高度
  maskClasses?: string; // Tailwind utility class，用於設定遮罩顏色與透明度
  classes?: string;
  imgAlign?: "left" | "center" | "right"; // 控制圖片的 object position
  isLoading?: boolean;
  skeletonSize?: "sm" | "lg";
}

const Banner = ({
  banner,
  priority = true,
  heightClasses,
  maskClasses,
  classes,
  imgAlign = "center",
  isLoading = false,
  skeletonSize = "sm",
}: BannerProps) => {
  return (
    <div
      className={clsx(
        "relative w-full overflow-hidden bg-base-200",
        heightClasses ? heightClasses : "brand-h-screen-minus-header",
        classes,
      )}
    >
      {isLoading ? (
        <div className="flex h-full items-center justify-center">
          <BannerSkeleton size={skeletonSize} />
        </div>
      ) : (
        <>
          <picture className="absolute inset-0">
            <source media={`(min-width:${BREAKPOINTS.lg}px)`} srcSet={banner.image.desktop} />
            <img
              src={banner.image.default}
              loading={priority ? "eager" : "lazy"}
              fetchPriority={priority ? "high" : "auto"}
              decoding="async"
              alt={"小院咖啡"}
              className={clsx("h-full w-full object-cover", {
                "object-left": imgAlign === "left",
                "object-center": imgAlign === "center",
                "object-right": imgAlign === "right",
              })}
            />
          </picture>
          <div
            className={clsx(
              "absolute inset-0 flex items-center justify-center p-12 text-center",
              maskClasses ? maskClasses : "bg-black/30",
            )}
          >
            <div
              className={clsx(
                "relative max-w-screen-md",
                styles["banner-gradient"],
                styles["banner-gradient-black"],
              )}
            >
              <h2 className="brand-heading-lg relative z-10 text-white lg:text-5xl lg:leading-normal">
                {banner.title}
              </h2>
              {banner.description && (
                <div className="brand-text-md relative z-10 mt-4 flex justify-center text-base-200 lg:mt-9">
                  {banner.description}
                </div>
              )}
              {banner.link && (
                <Link to={banner.link.href} className="brand-btn-base btn relative z-10 mt-9">
                  {banner.link.text}
                </Link>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const BannerSkeleton = ({ size = "sm" }) => (
  <div className="flex w-full max-w-md animate-pulse flex-col items-center justify-center gap-4 p-4 *:rounded-md *:bg-base-300">
    <div className="h-16 w-full"></div>
    {size === "lg" && <div className="mb-8 h-16 w-2/3"></div>}
    <div className="h-4 w-1/2"></div>
    <div className="h-4 w-1/2"></div>
    {size === "lg" && <div className="h-4 w-1/4"></div>}
  </div>
);

export default Banner;
